import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import type { ReactNode } from "react";
import { useCallback, useEffect } from "react";
import { Element, scroller } from "react-scroll";
import { ChatMessageRow } from "~/components/common/chat/messages/ChatMessageRow";
import type { Deal } from "~/models/Deal";
import type { Message, MessageSpeaker } from "~/models/Message";
import { postRead as postReadForClient } from "~/utils/api/deals/Message";
import { postRead as postReadForVendor } from "~/utils/api/vendors/deals/Message";
import Styles from "./ChatMessageArea.module.scss";

type Props = DeepReadonly<{
	deal: Deal;
	handleMutate: () => Promise<void>;
	isOpen: boolean;
	messages: Message[];
	speaker: MessageSpeaker;
}>;

export function ChatMessageArea({
	isOpen,
	deal,
	messages,
	speaker,
	handleMutate
}: Props) {
	useEffect(() => {
		if (!isOpen) return;

		const timer = setTimeout(async () => {
			const readMethod =
				speaker === "maintenance_company"
					? postReadForClient
					: postReadForVendor;
			if (
				deal.messageSummaryUnread?.unread ||
				deal.messageSummaryUnread?.displayUnread
			) {
				await readMethod(deal.id);
			}
			await handleMutate();
		}, 2000);
		return () => {
			clearTimeout(timer);
		};
	}, [deal, handleMutate, isOpen, speaker]);

	useEffect(() => {
		void handleMutate();
		scroller.scrollTo("chat-message-field-bottom", {
			containerId: "chat_messages_field",
			duration: 0,
			ignoreCancelEvents: true
		});
	}, [handleMutate, messages.length]);

	const renderRow = useCallback(
		(message: DeepReadonly<Message>): ReactNode => (
			<ChatMessageRow
				deal={deal}
				key={message.id}
				message={message}
				speaker={speaker}
			/>
		),
		[speaker, deal]
	);
	return (
		<div className={Styles.Messages} id="chat_messages_field">
			{messages.map(renderRow)}
			<Element name="chat-message-field-bottom" />
		</div>
	);
}
